import { useCallback, useEffect, useMemo, useRef, useState, MouseEvent as ReactMouseEvent } from "react";
import ReactFlow, { addEdge, Background, Controls, useEdgesState, useNodesState, Node } from "reactflow";
import Aside from "../../components/Aside";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import 'reactflow/dist/style.css';
import NodeType from "../../components/form-node/NodeType";
import { BotService } from "../../services/BotService";
import FormDefaultNodePage from "../bot/FormDefaultNodePage";
import FormHTTPNodePage from "../bot/FormHTTPNodePage";
import FormRedirectNodePage from "../bot/FormRedirectNodePage";
import FormCloseNodePage from "../bot/FormCloseNodePage";
import FormInteractiveNodePage from "../bot/FormInteractiveNodePage";
import CustomEdge from '../../components/form-node/CustomEdge';
import IfThen from "../../components/IfThen";
import Swal from 'sweetalert2'
import FormConnectionPage from "../bot/FormConnectionPage";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useTokenApi from "../../hooks/useTokenApi";
import PreviewChatPage from "../bot/PreviewChatPage";
import { BOT_STATUS, BOT_VERSIONS } from "../../constants";
import InfoNodeV2 from "../bot/InfoNodeV2";
import { useTranslation } from "react-i18next";
import useRequest from "../../hooks/useRequest";
import Loader from "../../components/Loader";
import usePermission from "../../hooks/usePermission";
import Input from "../../components/form-controls/Input";
import FlowDownloadDropdown from "../../components/flow-download/FlowDownloadDropdown";
declare const bootstrap: any;

const SUBACCOUNT_ID = null;

function BotBuilderFlowsPage() {
    const { t } = useTranslation();
    const { call, isRequesting } = useRequest();
    const navigate = useNavigate();
    const hasIntentionsAccess = usePermission('bots.intentions');
    const location = useLocation();
    const formData = location.state?.formData;
    const info_bot = location.state?.info_bot;
    const { state: { account_id, status_flow, name_flow, bot } } = useLocation()
    const { tokenApi } = useTokenApi();
    const dropdownNodesRef = useRef(null)
    const reactFlowWrapper = useRef(null);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [nodeSelected, setNodeSelected] = useState(null);
    const [edgeSelected, setEdgeSelected] = useState(null);
    const [isPreview, setIsPreview] = useState(false);

    const [environment, setEnvironment] = useState('1')

    const [chatbotInfo, setChatbotInfo] = useState(null);

    const nodeTypes = useMemo(() => ({ cardNodeType: NodeType }), []);
    const edgeTypes = useMemo(() => ({ custom: CustomEdge }), []);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    const handleConnector = async (connection) => {
        try {
            await BotService.handleConnector(connection)
        } catch (error) {
            console.error('BotBuilderPage.handleConnector.error::', error)
            Swal.fire('¡Error!', 'No se pudo guardar la conexión entre nodos, intenta nuevamente', 'error')
        }
    }

    const onConnect = useCallback((connection: any) => setEdges((eds) => {

        let data: any = {
            action: 'ADD',
            source: connection.source,
            target: connection.target
        }

        if (connection.sourceHandle) {
            const keyword = connection.sourceHandle;
            data.keywords = [keyword];
        }

        handleConnector(data)

        return addEdge({
            ...connection,
            selected: true,
            className: 'chatbot-edge',
            label: data.keywords ? data.keywords[0] : '',
            type: 'smoothstep',
        }, eds)
    }), [setEdges]);

    const closeDropdown = () => {
        const instance = bootstrap.Dropdown.getInstance(dropdownNodesRef.current)
        instance.hide();
    }

    const getTree = async () => {
        const response = await call(() => BotService.getTree(bot, environment))

        let treeResponse = response.data;

        let initialNodes$: any = []
        let initialEdges$: any = [];

        const keys = Object.keys(treeResponse)

        for (const key of keys) {
            const node = treeResponse[key];

            initialNodes$.push({
                id: node._id,
                type: 'cardNodeType',
                data: {
                    ...node,
                    handleSaveNode
                },
                position: node.position,
            })

            if (node.parent_nodes && node.parent_nodes.length) {

                const connections = node.parent_nodes.map(n => {
                    return {
                        id: `${node._id}-${n.node}`,
                        source: n.node,
                        target: node._id,
                        className: 'chatbot-edge',
                        type: 'smoothstep',
                        label: n.keywords && n.keywords[0] ? n.keywords.join(', ') : '',
                        sourceHandle: n.keywords && n.keywords[0] ? n.keywords[0] : '',
                    }
                })

                initialEdges$ = [...initialEdges$, ...connections];
            }
        }

        setNodes(initialNodes$)
        setEdges(initialEdges$)
    }

    const handleSaveNode = async ({ nodeID, data, chatbotInfo }) => {
        data.account_id = chatbotInfo.account_id;
        data.subaccount_id = SUBACCOUNT_ID;
        data.bot_id = bot !== undefined ? bot : info_bot.bot_id;

        const response = await BotService.saveNode(data);
        const nodeInfo = response.data;
        setNodes((nds) =>
            nds.map((n) => {
                if (n.id === nodeID) {
                    n.id = nodeInfo._id;
                    n.data = {
                        ...nodeInfo,
                        handleSaveNode
                    }
                }

                return n;
            })
        )
    }

    const updateCoordinates = async (event: ReactMouseEvent, node: Node) => {
        if (node.dragging) {
            await BotService.updateCoordinates({ _id: node.data._id, x: node.position.x, y: node.position.y })
        }
    }

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onSelectionChange = useCallback(({ nodes, edges }) => {
        setIsPreview(false)

        if (chatbotInfo?.version === BOT_VERSIONS.V2) {
            setEdgeSelected(null)
            nodes[0] ? setNodeSelected(nodes[0]) : setNodeSelected(null)
            return;
        }

        if (nodes[0]) {
            setNodeSelected(nodes[0])
            setEdgeSelected(null)
            return;
        } else {
            setNodeSelected(null)
        }


        const edge = edges[0] || null;

        if (edge) {
            const nodeSource = reactFlowInstance.getNode(edge.source)
            const nodeTarget = reactFlowInstance.getNode(edge.target)

            setEdgeSelected({
                nodeSource,
                nodeTarget,
                edge
            })
            setNodeSelected(null)
        } else {
            setEdgeSelected(null)
        }


    }, [reactFlowInstance, chatbotInfo]);


    const generateRandomId = () => (Math.random() + 1).toString(36).substring(2);

    const onDragStart = (event, nodeType) => {
        event.dataTransfer.setData('application/reactflow', nodeType);
        event.dataTransfer.effectAllowed = 'move';
    }

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
            const type = event.dataTransfer.getData('application/reactflow');

            if (typeof type === 'undefined' || !type) return;

            const position = reactFlowInstance.project({
                x: event.clientX - reactFlowBounds.left - 108,
                y: event.clientY - reactFlowBounds.top,
            });
            const newNode: any = {
                id: generateRandomId(),
                type: 'cardNodeType',
                data: {
                    type,
                    handleSaveNode
                },
                position,
                selected: true
            }

            setNodes(
                (nds) => nds.map(n => {
                    n.selected = false;
                    return n
                })
                    .concat(newNode)
            );

            closeDropdown()
        },
        [reactFlowInstance]
    )

    const deleteNode = async (id: string) => {
        const { isConfirmed } = await Swal.fire({
            title: t('campaigns.response-flow.delete-node'),
            text: t('campaigns.response-flow.delete-node-text'),
            icon: 'warning',
            confirmButtonText: t('btn-continue'),
            cancelButtonText: t('btn-cancel'),
            showCancelButton: true,

        })

        if (isConfirmed) {
            await BotService.deleteNode(id);
            await getTree()
        }
    }

    const deleteEdge = async (nodeSource: string, nodeTarget: string) => {
        const { isConfirmed } = await Swal.fire({
            title: t('campaigns.response-flow.delete-connection'),
            text: t('campaigns.response-flow.delete-connection-text'),
            icon: 'warning',
            confirmButtonText: t('btn-continue'),
            cancelButtonText: t('btn-cancel'),
            showCancelButton: true,

        })

        if (isConfirmed) {
            const data = {
                action: 'DELETE',
                source: nodeSource,
                target: nodeTarget
            }
            await handleConnector(data);
            await getTree()
        }
    }

    const updateEdge = async (nodeSource: string, nodeTarget: string, keywords: Array<string>) => {
        const data = {
            action: 'ADD',
            source: nodeSource,
            target: nodeTarget,
            keywords
        }

        await handleConnector(data);
        await getTree()
    }

    const getChatbotInfo = async () => {
        const { data } = await BotService.getChatbotInfo(account_id);
        data.bot_id = info_bot !== undefined ? info_bot.bot_id : bot;
        setChatbotInfo(data);
    }

    const chanceStatus = async () => {
        const { isConfirmed } = await Swal.fire({
            title: isActiveBot ? t('campaigns.response-flow.disable-chatbot') : t('campaigns.response-flow.enable-chatbot'),
            text: `${t('campaigns.response-flow.change-status-text-1')} ${isActiveBot ? t('campaigns.response-flow.disable') : t('campaigns.response-flow.enable')} ${t('campaigns.response-flow.change-status-text-2')}`,
            icon: 'warning',
            iconColor: '#f5ca99',
            confirmButtonText: t('btn-continue'),
            cancelButtonText: t('btn-cancel'),
            customClass: {
                cancelButton: 'btn btn-white',
                confirmButton: 'btn btn-primary me-3',
            },
            buttonsStyling: false,
            showConfirmButton: true,
            focusDeny: true,
            focusCancel: true,
            showCancelButton: true,
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                const newStatus = isActiveBot ? BOT_STATUS.INACTIVE : BOT_STATUS.ACTIVE

                await BotService.setStatus({
                    token: tokenApi,
                    bot_id: bot !== undefined ? bot : info_bot.bot_id,
                    status: newStatus
                })

                await getChatbotInfo();
            }
        })

        if (isConfirmed) {
             Swal.fire(t('campaigns.response-flow.changed-status-title'), t('campaigns.response-flow.update-successful'), 'success')
        }
    }

    const releaseChatbot = async () => {
        const { isConfirmed } = await Swal.fire({
            title: t('campaigns.response-flow.publish-chatbot'),
            text: t('campaigns.response-flow.publish-chatbot-text'),
            icon: 'warning',
            iconColor: '#f5ca99',
            cancelButtonText: t('btn-cancel'),
            confirmButtonText: t('btn-publish'),
            customClass: {
                cancelButton: 'btn btn-white',
                confirmButton: 'btn btn-primary me-3',
            },
            buttonsStyling: false,
            showConfirmButton: true,
            focusDeny: true,
            focusCancel: true,
            showCancelButton: true,
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                await BotService.deployBoy({
                    bot_id: bot !== undefined ? bot : info_bot.bot_id
                })

                await getChatbotInfo();
            }
        })

        if (isConfirmed) {
             Swal.fire(t('campaigns.response-flow.changed-status-title'), t('campaigns.response-flow.update-successful'), 'success')
        }
    }

    const previewFlow = () => {
        setNodeSelected(null)
        setEdgeSelected(null)
        setIsPreview(true)
    }

    const getTreeV2 = async () => {
        try {
            const { data } = await BotService.getTreeV2(chatbotInfo.account_id, environment)
            const bot = data.data;

            let initialNodes$ = [];
            let initialEdges$ = [];

            initialNodes$.push({
                id: `${bot.id}`,
                data: {
                    label: `${bot.id}: ${bot.name}`,
                    extra: bot
                },
                position: { x: +bot.x, y: +bot.y },
                type: 'input',
                sourcePosition: 'right',
                connectable: false,
                draggable: false,
            })

            const getChildren = (parent, children, nodesRec = [], edgesRec = []) => {
                for (const child of children) {
                    nodesRec.push({
                        id: `${child.id}`,
                        data: { label: `${child.name}`, extra: child },
                        position: { x: +child.x, y: +child.y },
                        type: 'default',
                        targetPosition: 'left',
                        sourcePosition: 'right',
                        connectable: false,
                        draggable: false,
                    })

                    edgesRec.push({
                        id: `${parent.id}-${child.id}`,
                        source: `${parent.id}`,
                        target: `${child.id}`,
                        // animated: true,
                    })

                    if (child.children && child.children.length) {
                        getChildren(child, child.children, nodesRec, edgesRec)
                    }
                }

                return { nodesRec, edgesRec };
            }

            if (bot.children && bot.children.length) {
                const { nodesRec, edgesRec } = getChildren(bot, bot.children)
                initialNodes$ = initialNodes$.concat(nodesRec)
                initialEdges$ = initialEdges$.concat(edgesRec)
            }

            setNodes(initialNodes$)
            setEdges(initialEdges$)
        } catch (error) {
            console.error(error)
        }
    }

    const handleVersionsBot = () => {
        // if (chatbotInfo.status === BOT_STATUS.NOT_SHOW) {
        //     navigate('/bot/activation')
        //     return
        // }

        if (chatbotInfo.version === BOT_VERSIONS.V2) {
            setIsPreview(true);
            return getTreeV2()
        }

        if (chatbotInfo.version === BOT_VERSIONS.V3) {
            return getTree();
        }
    }

    const isActiveBot = useMemo(() => {

        if (!chatbotInfo) return false;

        if (chatbotInfo.bot_version_production !== chatbotInfo.version) return false;

        return chatbotInfo.status === BOT_STATUS.ACTIVE
    }, [chatbotInfo])

    useEffect(() => {
        if (chatbotInfo) {
            handleVersionsBot()
        }
    }, [chatbotInfo, environment])


    useEffect(() => {
        getChatbotInfo()
    }, [])
    const heightRequesting = useMemo(() => { return isRequesting ? 'h-400' : '' }, [isRequesting]);
    return (
        <>
            <Header />
            <Aside mini />

            <main id="content" role="main" className="main">

                <div className="content container-fluid" >

                    <div className="page-header">
                        <div className="row align-items-center">
                            <div className="col-sm mb-2 mb-sm-0">
                                <nav aria-label="breadcrumb" className="mb-3">
                                    <ol className="breadcrumb breadcrumb-no-gutter">
                                        <li className="breadcrumb-item "><span className="breadcrumb-link" >{t('campaigns.monitoring.breadcrumb-campaigns')}</span></li>
                                        <li className="breadcrumb-item "><span className="breadcrumb-link" >{t("platform.chatbot.chatbot-title")}</span></li>
                                        <li className="breadcrumb-item active" aria-current="page">{t('campaigns.response-flow.flow-builder-title')}</li>
                                        <li className="breadcrumb-item active" aria-current="page"></li>
                                    </ol>
                                </nav>
                                <div className="col-sm mb-2 mb-sm-0 d-flex align-items-center">
                                    <button title="return" type="button" className="btn bg-soft-info-alternative btn-icon me-3 mb-2" onClick={() => navigate(-1)}>
                                        <i className="bi-chevron-left text-white-dark fw-bold"></i>
                                    </button>
                                    <h1 className="page-header-title text-white-dark">{name_flow || formData.name}</h1>
                                    <div className="ms-3 mb-2">
                                        {(status_flow === 'active' || (info_bot && info_bot.status === 'active')) ? (
                                            <span className="badge bg-soft-info text-info">
                                                <i className="bi bi-check-circle-fill fs-6"></i>
                                                <span className="fs-6 ms-1">{t("platform.chatbot.active")}</span>
                                            </span>
                                        ) : (status_flow === 'inactive' || (info_bot && info_bot.status === 'inactive')) ? (
                                            <span className="badge bg-soft-danger text-danger">
                                                <i className="bi bi-x-circle-fill fs-6"></i>
                                                <span className="fs-6 ms-1">{t("platform.chatbot.inactive")}</span>
                                            </span>
                                        ) : (
                                            <span className="badge bg-soft-success text-success">
                                                <i className="bi bi-dash-circle-fill fs-6"></i>
                                                <span className="fs-6 ms-1">{t("platform.chatbot.no-config")}</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="col-auto d-flex align-items-center ">
                                {
                                    chatbotInfo?.version === BOT_VERSIONS.V2 && (
                                        <div className="row">
                                            <div className="col">
                                                <div className="alert alert-warning px-5" role="alert">
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-shrink-0">
                                                            <img className="avatar avatar-xl" src="/assets/svg/illustrations/oc-megaphone.svg" alt="Image Description" data-hs-theme-appearance="default" />
                                                        </div>

                                                        <div className="flex-grow-1 ms-3">
                                                            <h3 className="alert-heading mb-1">{t('campaigns.response-flow.old-chatbot-warning')}</h3>
                                                            <p className="mb-0">{t('campaigns.response-flow.old-chatbot-warning-text')}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }

                                <div className="me-3">
                                    <select title="select" className="form-select" onChange={({ target: { value } }) => setEnvironment(value)} value={environment}>
                                        <option value={''} disabled>{t("platform.chatbot.ambient-select")}</option>
                                        <option value="1">{t("platform.chatbot.test-select")}</option>
                                        <option value="0">{t("platform.chatbot.production-select")}</option>
                                    </select>
                                </div>

                                {
                                    chatbotInfo?.version === BOT_VERSIONS.V3 && (
                                        <div className="dropdowm  me-3">
                                            <button className="btn bg-soft-info-alternative dropdown-toggle text-white-dark" id="moreOptionsDropdown" data-bs-toggle="dropdown" aria-expanded="true" ref={dropdownNodesRef} disabled={hasIntentionsAccess ? false : true}>
                                                <i className="bi-plus-lg me-2 fs-4"></i> {t("platform.chatbot.add-node")}
                                            </button>


                                            <div className="dropdown-menu mt-1" aria-labelledby="moreOptionsDropdown" data-popper-placement="bottom-start">
                                                <div className="dropdown-item disabled text-muted"  >
                                                    {t("platform.chatbot.node-drag")}
                                                </div>
                                                <div className="dropdown-item cursor-grab" onDragStart={(event) => onDragStart(event, 'DEFAULT')} draggable>
                                                    <i className="bi-justify-left dropdown-item-icon"></i> {t("platform.chatbot.default")}
                                                </div>
                                                <div className="dropdown-item cursor-grab" onDragStart={(event) => onDragStart(event, 'INTERACTIVE')} draggable>
                                                    <i className="bi-chat-dots dropdown-item-icon"></i> {t("platform.chatbot.interactive")}
                                                </div>
                                                <div className="dropdown-item cursor-grab" onDragStart={(event) => onDragStart(event, 'HTTP')} draggable>
                                                    <i className="bi-hdd-network dropdown-item-icon"></i> {t("platform.chatbot.validation")}
                                                </div>
                                                <div className="dropdown-item cursor-grab" onDragStart={(event) => onDragStart(event, 'REDIRECT_AGENT')} draggable>
                                                    <i className="bi-people dropdown-item-icon"></i> {t("platform.chatbot.redirect")}
                                                </div>
                                                <div className="dropdown-item cursor-grab" onDragStart={(event) => onDragStart(event, 'CLOSE')} draggable>
                                                    <i className="bi-slash-circle dropdown-item-icon"></i> {t("platform.chatbot.finish")}
                                                </div>

                                            </div>
                                        </div>
                                    )
                                }

                                <FlowDownloadDropdown
                                    className="me-3"
                                    reactFlowInstance={reactFlowInstance}
                                    reactFlowWrapperRef={reactFlowWrapper}
                                    chatbotName={(chatbotInfo as any)?.name}
                                    flowName={name_flow || formData?.name}
                                />

                                <button type="button" className="btn bg-soft-info-alternative me-3 text-white-dark" onClick={previewFlow} disabled={hasIntentionsAccess ? false : true}><i className="bi-play-fill fs-4"></i> {t("platform.chatbot.preview")}</button>

                                {chatbotInfo?.version === BOT_VERSIONS.V3 && !isActiveBot && environment === '0' && <button type="button" className="btn btn-primary me-3" onClick={chanceStatus} disabled={hasIntentionsAccess ? false : true}>{t("platform.chatbot.enable")}</button>}
                                {chatbotInfo?.version === BOT_VERSIONS.V3 && isActiveBot && environment === '0' && <button type="button" className="btn btn-soft-danger me-3" onClick={chanceStatus} disabled={hasIntentionsAccess ? false : true}>{t("platform.chatbot.disable")}</button>}
                                {chatbotInfo?.version === BOT_VERSIONS.V3 && environment === '1' && <button type="button" className="btn btn-success" onClick={releaseChatbot} disabled={hasIntentionsAccess ? false : true}>{t("platform.chatbot.submit")}</button>}
                            </div>
                        </div>
                    </div>
                    <div className={`row relative ${heightRequesting}`}>
                        {
                            isRequesting ? <Loader /> :
                                <>
                                    <div className="col-sm-9">
                                        <div className="card ">
                                            <div className="card-body p-0">
                                                <div className="reactflow-wrapper" ref={reactFlowWrapper} style={{ height: 850 }}>
                                                    <ReactFlow
                                                        nodes={nodes}
                                                        edges={edges}
                                                        nodeTypes={nodeTypes}
                                                        edgeTypes={edgeTypes}
                                                        onNodesChange={onNodesChange}
                                                        onNodeDragStop={updateCoordinates}
                                                        onEdgesChange={onEdgesChange}
                                                        onConnect={onConnect}
                                                        onInit={setReactFlowInstance}
                                                        onDrop={onDrop}
                                                        onDragOver={onDragOver}
                                                        onSelectionChange={onSelectionChange}
                                                        fitView
                                                    >
                                                        <Controls />
                                                        <Background />
                                                    </ReactFlow>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-sm-3 content-start">
                                        <div className="card" style={{ height: 850, overflowX: 'hidden' }}>

                                            <IfThen condition={isPreview}>
                                                <PreviewChatPage chatbotInfo={chatbotInfo} environment={environment} onClose={() => setIsPreview(false)} />
                                            </IfThen>

                                            <IfThen condition={!nodeSelected && !edgeSelected && !isPreview}>
                                                <div className="card-body d-flex align-items-center text-center">
                                                    <div className="alert alert-soft-info">
                                                        <p className="h2"><i className="bi bi-robot"></i></p>
                                                        <p>{t("platform.chatbot.chatbot-p")}</p>
                                                    </div>
                                                </div>
                                            </IfThen>

                                            <IfThen condition={chatbotInfo?.version === BOT_VERSIONS.V2 && nodeSelected}>
                                                <InfoNodeV2 node={nodeSelected} environment={environment} />
                                            </IfThen>

                                            <IfThen condition={nodeSelected?.data?.type === 'DEFAULT'}>
                                                <FormDefaultNodePage node={nodeSelected} onDelete={(id) => deleteNode(id)} chatbotInfo={chatbotInfo} />
                                            </IfThen>
                                            <IfThen condition={nodeSelected?.data?.type === 'INTERACTIVE'}>
                                                <FormInteractiveNodePage node={nodeSelected} onDelete={(id) => deleteNode(id)} chatbotInfo={chatbotInfo} />
                                            </IfThen>
                                            <IfThen condition={nodeSelected?.data?.type === 'HTTP'}>
                                                <FormHTTPNodePage node={nodeSelected} onDelete={(id) => deleteNode(id)} chatbotInfo={chatbotInfo} />
                                            </IfThen>
                                            <IfThen condition={nodeSelected?.data?.type === 'REDIRECT_AGENT'}>
                                                <FormRedirectNodePage node={nodeSelected} onDelete={(id) => deleteNode(id)} chatbotInfo={chatbotInfo} />
                                            </IfThen>
                                            <IfThen condition={nodeSelected?.data?.type === 'CLOSE'}>
                                                <FormCloseNodePage node={nodeSelected} onDelete={(id) => deleteNode(id)} chatbotInfo={chatbotInfo} />
                                            </IfThen>

                                            <IfThen condition={edgeSelected}>
                                                <FormConnectionPage connection={edgeSelected} onUpdate={(source, target, keywords) => updateEdge(source, target, keywords)} onDelete={(source, target) => deleteEdge(source, target)} />
                                            </IfThen>

                                        </div>

                                    </div>
                                </>
                        }
                    </div>

                </div>

                <Footer />
            </main>
        </>
    );
}

export default BotBuilderFlowsPage;
